import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

/**
 * BreadcrumbContext lets dynamic pages publish a human-readable label for
 * their current URL (e.g. /courses/:id -> "Foundation Course") so the global
 * <Breadcrumbs /> in the navbar can render the correct trail without each
 * page rendering its own breadcrumb.
 */
const BreadcrumbContext = createContext({
    labels: {},
    setLabel: () => {},
    clearLabel: () => {},
});

export function BreadcrumbProvider({ children }) {
    const [labels, setLabels] = useState({});

    const setLabel = useCallback((path, label) => {
        if (!path) return;
        setLabels((prev) => {
            if (prev[path] === label) return prev;
            return { ...prev, [path]: label };
        });
    }, []);

    const clearLabel = useCallback((path) => {
        setLabels((prev) => {
            if (!(path in prev)) return prev;
            const next = { ...prev };
            delete next[path];
            return next;
        });
    }, []);

    return (
        <BreadcrumbContext.Provider value={{ labels, setLabel, clearLabel }}>
            {children}
        </BreadcrumbContext.Provider>
    );
}

export function useBreadcrumbs() {
    return useContext(BreadcrumbContext);
}

/**
 * Convenience hook for dynamic pages — publishes a label for the given
 * pathname while mounted. Pass `null` while data is still loading and the
 * breadcrumb will gracefully fall back to the URL segment.
 */
export function useBreadcrumbLabel(path, label) {
    const { setLabel, clearLabel } = useBreadcrumbs();
    const prevPathRef = useRef(null);

    useEffect(() => {
        if (path && label) {
            setLabel(path, label);
            prevPathRef.current = path;
        }
        return () => {
            if (prevPathRef.current) clearLabel(prevPathRef.current);
        };
    }, [path, label, setLabel, clearLabel]);
}
